import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, input, OnInit } from '@angular/core';
import {
   AbstractControl,
   ControlContainer,
   ControlValueAccessor,
   FormArray,
   FormBuilder,
   FormGroup,
   FormGroupDirective,
   FormsModule,
   NG_VALUE_ACCESSOR,
   NgControl,
   ReactiveFormsModule,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { MaterialModule } from '@core/material.module';
import { NotificationService } from '@core/services/notification.service';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconComponent } from 'angular-tabler-icons';
import { CURRENCY_MASK_CONFIG, CurrencyMaskConfig, CurrencyMaskModule } from 'ng2-currency-mask';
import { OnlyNumbersDirective } from 'src/app/shared/directives/only-numbers.directive';
import { ButtonToggleComponent } from '../button-toggle/button-toggle.component';

export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
   align: 'right',
   allowNegative: true,
   decimal: ',',
   precision: 2,
   prefix: '',
   suffix: '',
   thousands: '.',
};

@Component({
   selector: 'app-fees-range',
   standalone: true,
   imports: [
      CommonModule,
      TablerIconComponent,
      TranslateModule,
      MaterialModule,
      FormsModule,
      ReactiveFormsModule,
      CurrencyMaskModule,
      OnlyNumbersDirective,
      ButtonToggleComponent,
   ],
   providers: [
      {
         provide: CURRENCY_MASK_CONFIG,
         useValue: CustomCurrencyMaskConfig,
      },
   ],
   viewProviders: [
      {
         provide: ControlContainer,
         useExisting: FormGroupDirective,
      },
      {
         provide: NG_VALUE_ACCESSOR,
         multi: true,
         useExisting: forwardRef(() => FeesRangeComponent),
      },
   ],
   templateUrl: './fees-range.component.html',
})
export class FeesRangeComponent implements OnInit, ControlValueAccessor {
   private formBuilder = inject(FormBuilder);
   private notificationService = inject(NotificationService);
   private parent = inject(FormGroupDirective);
   ngControl = inject(NgControl, { optional: true, self: true })!;

   form: FormGroup = new FormGroup({});

   readonly fees = input<any[]>();
   readonly feeByNeighborhoods = input<any[]>();
   readonly deliveryFeeType = input<number>();

   readonly nameControl = input<string>('Fees');
   readonly nameControlByNeighborhoods = input<string>('FeeByNeighborhoods');
   readonly viewMode = input(false);

   optionsButtonToogle = [
      { Name: 'ByKm', Id: 1 },
      { Name: 'ByNeighborhoods', Id: 2 },
   ];

   get feesArray(): FormArray {
      return this.form.get(this.nameControl()) as FormArray;
   }

   get feesByNeighborhoodArray(): FormArray {
      return this.form.get(this.nameControlByNeighborhoods()) as FormArray;
   }

   get f() {
      return this.form.controls;
   }

   getDeliveryFeeTypeId(): number | null {
      const value = this.f['DeliveryFeeType']?.value;
      if (value == null) {
         return null;
      }

      return typeof value === 'object' ? value.Id : value;
   }

   canAddNewFee(): boolean {
      if (this.viewMode()) {
         return false;
      }

      const deliveryFeeType = this.getDeliveryFeeTypeId();
      if (deliveryFeeType === 1) {
         return this.isActiveFeesArrayValid(this.feesArray);
      }

      if (deliveryFeeType === 2) {
         return this.isActiveFeesArrayValid(this.feesByNeighborhoodArray);
      }

      return false;
   }

   private isActiveFeesArrayValid(array: FormArray | null): boolean {
      if (!array?.enabled) {
         return false;
      }

      array.updateValueAndValidity({ emitEvent: false });

      return !array.controls.some((group) => {
         if (!group.enabled) {
            return false;
         }

         group.updateValueAndValidity({ emitEvent: false });
         return group.invalid;
      });
   }

   writeValue(value: any): void {
      this.f[this.nameControl()].value;
   }

   onChange: (_: any) => void = (value: any) => {
      this.f[this.nameControl()].setValue(value);
   };

   onTouched: () => void = () => {};

   registerOnChange(fn: any): void {
      this.onChange = fn;
   }

   registerOnTouched(fn: any): void {
      this.onTouched = fn;
   }

   ngOnInit() {
      if (this.ngControl != null) this.ngControl.valueAccessor = this;
      this.form = this.parent.form;
      if (!this.f[this.nameControl()]) {
         this.form.addControl(this.nameControl(), this.formBuilder.array([]));
      }

      if (!this.form.get(this.nameControlByNeighborhoods())) {
         this.form.addControl(this.nameControlByNeighborhoods(), this.formBuilder.array([]));
      }

      if (!this.form.get('DeliveryFeeType')) {
         this.form.addControl(
            'DeliveryFeeType',
            this.formBuilder.control(this.deliveryFeeType() ?? 1),
         );
      }

      const fees: any[] = [];
      const feesByNeighborhood: any[] = [];

      const feesValue = this.fees();
      const feeByNeighborhoodsValue = this.feeByNeighborhoods();

      if (feesValue && feesValue.length > 0) {
         feesValue.forEach((fee: any) => {
            fees.push({
               MaxRange: fee.MaxRange,
               Value: fee.Value,
               TimeToDeliver: fee.TimeToDeliver,
               Neighborhood: fee.Neighborhood,
               DeliveryFeeType: fee.DeliveryFeeType,
            });
         });
      }

      if (feeByNeighborhoodsValue && feeByNeighborhoodsValue.length > 0) {
         feeByNeighborhoodsValue.forEach((fee: any) => {
            feesByNeighborhood.push({
               Neighborhood: fee.Neighborhood,
               Value: fee.Value,
               TimeToDeliver: fee.TimeToDeliver,
            });
         });
      }

      this.onSetNewFee(fees, feesByNeighborhood);
   }

   onSetNewFee(fees: any = null, feesByNeighborhoods: any = null) {
      // Prefer in-memory FormArray on remount; hydrate from input only when empty.
      if (fees?.length && this.feesArray.length === 0) {
         fees.forEach((fee: any) => {
            this.feesArray.push(this.onCreateFormGroupFee(fee));
         });
      }

      if (feesByNeighborhoods?.length && this.feesByNeighborhoodArray.length === 0) {
         feesByNeighborhoods.forEach((feeNeighborhood: any) => {
            this.feesByNeighborhoodArray.push(
               this.onCreateFormGroupFeesByNeighborhood(feeNeighborhood),
            );
         });
      }
   }

   onCreateFormGroupFee(fee: any = null): FormGroup {
      const rowIndex = this.feesArray.length;
      const rangeFrom =
         rowIndex > 0
            ? (this.toNumber(this.feesArray.at(rowIndex - 1).get('MaxRange')?.value) ?? 0)
            : 0;

      const group = this.formBuilder.group({
         RangeFrom: [fee?.RangeFrom ?? rangeFrom],
         MaxRange: [fee?.MaxRange, [Validators.required, this.maxRangeOrderValidator()]],
         Value: [fee?.Value, Validators.required],
         TimeToDeliver: [fee?.TimeToDeliver, Validators.required],
      });

      group.get('MaxRange')?.valueChanges.subscribe(() => {
         this.onKmMaxRangeChanged(group);
      });

      group.valueChanges.subscribe(() => {
         this.form.updateValueAndValidity({ emitEvent: false });
      });

      return group;
   }

   private maxRangeOrderValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const group = control.parent as FormGroup | null;
         const feesArray = this.feesArray;

         if (!group || !feesArray) {
            return null;
         }

         const rowIndex = feesArray.controls.indexOf(group);
         if (rowIndex < 0) {
            return null;
         }

         const maxRange = this.toNumber(control.value);
         if (maxRange == null) {
            return null;
         }

         const rangeFrom = this.toNumber(group.get('RangeFrom')?.value) ?? 0;
         if (maxRange <= rangeFrom) {
            return { maxRangeOrder: true };
         }

         const previousGroup =
            rowIndex > 0 ? (feesArray.at(rowIndex - 1) as FormGroup | null) : null;
         const previousMaxRange = this.toNumber(previousGroup?.get('MaxRange')?.value);
         if (previousMaxRange != null && maxRange <= previousMaxRange) {
            return { maxRangeOrder: true };
         }

         const nextGroup =
            rowIndex < feesArray.length - 1
               ? (feesArray.at(rowIndex + 1) as FormGroup | null)
               : null;
         const nextMaxRange = this.toNumber(nextGroup?.get('MaxRange')?.value);
         if (nextMaxRange != null && maxRange >= nextMaxRange) {
            return { maxRangeOrder: true };
         }

         return null;
      };
   }

   onMaxRangeBlur(index: number): void {
      const control = this.feesArray.at(index)?.get('MaxRange');
      if (!control) {
         return;
      }

      control.markAsTouched();
      control.updateValueAndValidity();

      if (control.hasError('maxRangeOrder')) {
         this.notificationService.showDialogError('ValidateMessageDeliveryFeeMaxRangeOrder');
      }
   }

   private onKmMaxRangeChanged(changedGroup: FormGroup): void {
      const changedIndex = this.feesArray.controls.indexOf(changedGroup);
      if (changedIndex < 0) {
         return;
      }

      const previousMaxRange = this.toNumber(changedGroup.get('MaxRange')?.value);
      const nextGroup = this.feesArray.at(changedIndex + 1) as FormGroup | undefined;

      if (nextGroup) {
         nextGroup.get('RangeFrom')?.setValue(previousMaxRange ?? 0, { emitEvent: false });
      }

      this.feesArray.controls.forEach((control) => {
         control.get('MaxRange')?.updateValueAndValidity({ emitEvent: false });
      });

      this.form.updateValueAndValidity();
   }

   private toNumber(value: unknown): number | null {
      if (value === null || value === undefined || value === '') {
         return null;
      }

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
   }

   onCreateFormGroupFeesByNeighborhood(feesByNeighborhoods: any = null): FormGroup {
      const group = this.formBuilder.group({
         Neighborhood: [feesByNeighborhoods?.Neighborhood, Validators.required],
         Value: [feesByNeighborhoods?.Value, Validators.required],
         TimeToDeliver: [feesByNeighborhoods?.TimeToDeliver, Validators.required],
      });

      group.valueChanges.subscribe(() => {
         this.form.updateValueAndValidity({ emitEvent: false });
      });

      return group;
   }

   onRemoveFee(index: number) {
      const deliveryFeeType = this.getDeliveryFeeTypeId();
      if (deliveryFeeType === 1) {
         this.feesArray.removeAt(index);
         this.syncKmRangeFromValues();
         this.feesArray.controls.forEach((control) => {
            control.get('MaxRange')?.updateValueAndValidity({ emitEvent: false });
         });
      } else if (deliveryFeeType === 2) {
         this.feesByNeighborhoodArray.removeAt(index);
      }

      this.form.updateValueAndValidity();
   }

   private syncKmRangeFromValues(): void {
      this.feesArray.controls.forEach((control, index) => {
         const rangeFrom =
            index > 0
               ? (this.toNumber(this.feesArray.at(index - 1).get('MaxRange')?.value) ?? 0)
               : 0;
         control.get('RangeFrom')?.setValue(rangeFrom, { emitEvent: false });
      });
   }

   onAddNewFee() {
      const deliveryFeeType = this.getDeliveryFeeTypeId();

      if (deliveryFeeType === 1) {
         this.feesArray.push(this.onCreateFormGroupFee());
      } else if (deliveryFeeType === 2) {
         this.feesByNeighborhoodArray.push(this.onCreateFormGroupFeesByNeighborhood());
      }

      this.form.updateValueAndValidity();
   }
}
